<template>
  <div class="staff-nominations-overview">
    <div class="staff-nominations-overview__inner">
      <div class="staff-nominations-overview__filters">
        <div class="FilterAspect">
          <md-field class="DateRange">
            <label for="selectedDateRange">Date filter</label>
            <md-select
              @input="dateRangeChanged"
              :value="selectedDateRange"
              class="box"
            >
              <md-option value="all_time">All time</md-option>
              <md-option value="year_to_date">Year to Date</md-option>
              <md-option value="custom">Custom Date Range</md-option>
            </md-select>
          </md-field>

          <div
            v-if="isCustomDatePickerVisible"
            class="Report__Filter__DateRange"
          >
            <md-field
              class="md-override Report__Filter__DateRange-field"
              md-inline
            >
              <md-datepicker
                class="md-override Report__Filter__DateRange-picker"
                v-model="selectedStartDate"
                md-immediately
                style="width: 208px; overflow: hidden;"
              >
                <label>Start date</label>
              </md-datepicker>
            </md-field>

            <md-field
              class="md-override Report__Filter__DateRange-field"
              md-inline
            >
              <md-datepicker
                class="md-override Report__Filter__DateRange-picker"
                v-model="selectedEndDate"
                md-immediately
                style="width: 208px; overflow: hidden;"
              >
                <label>End date</label>
              </md-datepicker>
            </md-field>
          </div>
        </div>

        <!-- Organization filter -->
        <div v-if="isStaff" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Organization</h5>
          <md-field>
            <label for="status">Organization</label>
            <md-select
              name="status"
              id="status"
              @md-selected="refresh"
              v-model="organizations"
              multiple
            >
              <md-option
                v-for="status in organizationOptions"
                :value="status.value"
                :key="status.value"
              >
                {{ status.label }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('organizations')"
              class="icon-container"
              v-if="hasArrayFilter('organizations')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>

        <div class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Status</h5>
          <md-field>
            <label for="status">Status</label>
            <md-select
              name="status"
              id="status"
              @md-selected="refresh"
              v-model="nominations.filters.statuses"
              multiple
            >
              <md-option
                v-for="status in statusOptions"
                :value="status.value"
                :key="status.value"
              >
                {{ status.label }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('statuses')"
              class="icon-container"
              v-if="hasArrayFilter('statuses')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>

        <div v-if="organizationDetails.isTcrOrganization && selectableSubOrganizations().length" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Sub Organization</h5>
          <md-field>
            <label for="suborganizations">Sub Organizations</label>
            <md-select
              name="organizations"
              @md-selected="refresh"
              v-model="nominations.filters.subOrganizations"
              multiple
            >
              <md-option v-for="subOrg in selectableSubOrganizations()" :value="subOrg.id" :key="subOrg.id">
                {{ subOrg.name }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('subOrganizations')"
              class="icon-container"
              v-if="hasArrayFilter('subOrganizations')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>

        <div class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Name</h5>
          <md-field>
            <label for="suborganizations">Name</label>
            <md-input
              name="organizations"
              v-model="nomineeFullName"
            >
            </md-input>
            <div
              @click="clearStringFilter('nomineeFullName')"
              class="icon-container"
              v-if="hasStringFilter('nomineeFullName')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>
      </div>

      <div class="staff-nominations-overview__table">
        <TableHeader :title="'Nominations'" :collection="nominations" />
        <LoadSpinner v-if="nominations.refreshing" style="padding: 2rem 0" />
        <!-- Upward Fund nominations -->
        <div v-if="organizationDetails.isTcrOrganization || user.isStaff" class="table-wrapper">
          <md-table>
            <md-table-row class="header-row">
              <md-table-head>
                Name
              </md-table-head>
              <md-table-head>Nominated By</md-table-head>
              <md-table-head>
                Status
              </md-table-head>
              <md-table-head>
                Email
              </md-table-head>
              <md-table-head>
                Nomination ID
              </md-table-head>
              <md-table-head>
                Nominator Organization
              </md-table-head>
              <md-table-head> Date Nominated </md-table-head>
            </md-table-row>

            <md-table-row
              class="body-row"
              v-for="nomination in nominations.list"
              :key="nomination.id"
              @click.native="() => onRowClick(nomination)"
            >
              <md-table-cell>{{
                nomination.nomineeFullName
              }}</md-table-cell>
              <md-table-cell>{{
                nomination.nominatedBy
              }}</md-table-cell>
              <md-table-cell>{{
                displayStatus(nomination.status)
              }}</md-table-cell>

              <md-table-cell>{{
                nomination.nomineeEmail
              }}</md-table-cell>
              <md-table-cell>{{
                nomination.id
              }}</md-table-cell>
              <md-table-cell>{{
                nomination.nominatorOrganization
              }}</md-table-cell>
              <md-table-cell>{{
                nomination.dateNominated | formatDateShort($language.current)
              }}</md-table-cell>
            </md-table-row>
          </md-table>
        </div>

        <!-- Northwestern nominations -->
        <div v-if="organizationDetails.isNorthwestern" class="table-wrapper">
          <md-table>
            <md-table-row class="header-row">
              <md-table-head>
                Name
              </md-table-head>
              <md-table-head>
                Status
              </md-table-head>
              <md-table-head>
                Email
              </md-table-head>
              <md-table-head>
                Nomination ID
              </md-table-head>
              <md-table-head> Date Nominated </md-table-head>
            </md-table-row>

            <md-table-row
              class="body-row"
              v-for="nomination in nominations.list"
              :key="nomination.id"
              @click.native="() => onRowClick(nomination)"
            >
              <md-table-cell>{{
                nomination.nomineeFullName
              }}</md-table-cell>
              <md-table-cell>{{
                displayStatus(nomination.status)
              }}</md-table-cell>

              <md-table-cell>{{
                nomination.nomineeEmail
              }}</md-table-cell>
              <md-table-cell>{{
                nomination.id
              }}</md-table-cell>
              <md-table-cell>{{
                nomination.dateNominated | formatDateShort($language.current)
              }}</md-table-cell>
            </md-table-row>
          </md-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { LoadSpinner, TableHeader } from '@/components'

import NominationsApi from '@/services/nominations'
import Nomination from '@/services/nominations/models.js'
import { NOMINATION_STATUS_CHOICES, NOMINATION_ORGANIZATION_CHOICES } from '@/services/nominations/options'
import CollectionManager from '@/services/collectionManager'
import Pagination from '@/services/pagination'
import Organization from '@/services/organizations'
import SelectOrganization from '@/services/selectOrganizations'

export default {
  name: 'NominationsOverview',
  components: {
    LoadSpinner,
    TableHeader,
  },
  data() {
    return {
      loading: true,
      refreshing: true,
      selectedDateRange: 'all_time',
      isCustomDatePickerVisible: false,
      selectedStartDate: null,
      selectedEndDate: null,
      nomineeFullName: '',
      organizations: [],
      nominations: CollectionManager.create({
        ModelClass: Nomination,
        pagination: Pagination.create({ pageSize: 50 }),
        filters: {
          ordering: '-datetime_created',
          status: [],
          subOrganizations: [],
          nomineeFullName: '',
          startDate: null,
          endDate: null,
          organizations: [],
        },
      }),
      selectableOrganizations: CollectionManager.create({
        ModelClass: SelectOrganization,
        pagination: Pagination.create({ pageSize: 100 }),
      }),
    }
  },
  async mounted() {
    this.loading = false
    this.selectableOrganizations.refresh()
    this.nominations.refresh()
  },
  methods: {
    onRowClick(nomination) {
      const routeData = this.$router.resolve({
        name: 'StaffNominationReview',
        params: {
          id: nomination.id,
        },
      })

      window.open(routeData.href, '_blank')
    },
    hasArrayFilter(filterKey) {
      let container = this.nominations.filters[filterKey]
      return container ? container.length > 0 : false
    },
    hasStringFilter(filterKey) {
      return this.nominations.filters[filterKey]
    },
    clearArrayFilter(filterKey) {
      this[filterKey] = []
    },
    clearStringFilter(filterKey) {
      this[filterKey] = ''
    },
    refresh() {
      this.nominations.refresh()
    },
    displayStatus(status) {
      let choice = Nomination.options.NOMINATION_STATUS_CHOICES.find(choice => choice.value == status)
      return this.$gettext(choice.label)
    },
    selectableSubOrganizations() {
      if (this.nominations.filters.organizations.length > 0 && this.selectableOrganizations.list) {
        const selectedOrgs = this.selectableOrganizations.list.filter(
          (org) => this.nominations.filters.organizations.includes(org.name)
        )
        const subOrgs = selectedOrgs.map((org) => {
          return org.subOrganizations.map((subOrg) => {
            return {...subOrg, orgName: org}
          })
        }).flat()
        return subOrgs
      }

      if(this.selectableOrganizations.list) {
        const availableOrganizations = this.selectableOrganizations.list

        const subOrgs = availableOrganizations.map((org) => {
          return org.subOrganizations.map((subOrg) => {
            return {...subOrg, orgName: org.name}
          })
        }).flat()

        return subOrgs
      }

      return []
    },
    dateRangeChanged(value) {
      if (value == this.selectedDateRange) { return; }

      this.selectedDateRange = value;

      switch (this.selectedDateRange) {
        case "all_time":
          this.isCustomDatePickerVisible = false;
          this.setNominationsDateFilter(null);
          break;

        case "year_to_date":
          this.isCustomDatePickerVisible = false;
          const now = new Date(Date.now());
          const prevYear = now.getFullYear() - 1;
          const yearAgo = new Date(new Date(Date.now()).setFullYear(prevYear));

          const filter = {
            startDate: yearAgo,
            endDate: now
          };

          this.setNominationsDateFilter(filter);
          break;

        case "custom":
          this.isCustomDatePickerVisible = true;
          this.buildCustomDateRangeFilter();
          break;
      }
    },
    buildCustomDateRangeFilter() {
      const start = this.selectedStartDate;
      const end = this.selectedEndDate;

      if (!start || !end) {
        return
      }

      const startTimestamp = Date.parse(start);
      const endTimestamp = Date.parse(end);
      if (isNaN(startTimestamp) || isNaN(endTimestamp)) {
        return;
      }

      const startDate = new Date(startTimestamp);
      const endDate = new Date(endTimestamp);

      this.setNominationsDateFilter({ startDate, endDate });
    },
    dateValueChanged(newValue, oldValue) {
      // Work around md-datepicker giving us a string instead of a Date
      const newTimestamp = newValue ? Date.parse(newValue) : null;
      const oldTimestamp = oldValue ? Date.parse(oldValue) : null;
      if (isNaN(newTimestamp) || isNaN(oldTimestamp)) { return }

      // Work around md-datepicker firing two identical events for a single
      // change event.
      // See https://github.com/vuematerial/vue-material/issues/2103
      if (oldTimestamp == newTimestamp) { return }

      const newDate = new Date(newTimestamp);
      const oldDate = new Date(oldTimestamp);

      // Work around md-datepicker triggering two watch events with different
      // timezone data for the same change event
      if (
        oldTimestamp && newTimestamp &&
        (
          (new Date(oldTimestamp)).toDateString() == (new Date(newTimestamp)).toDateString()
        )
      ) {
        return;
      }

      this.buildCustomDateRangeFilter();
    },
    setNominationsDateFilter(filter) {
      this.nominations.filters.startDate = filter?.startDate
      this.nominations.filters.endDate = filter?.endDate
      this.nominations.refresh()
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars']),
    isStaff() {
      return this.user.isStaff
    },
    statusOptions() {
      return Nomination.options.NOMINATION_STATUS_CHOICES
    },
    organizationOptions() {
      return Nomination.options.NOMINATION_ORGANIZATION_CHOICES
    },
  },
  watch: {
    nomineeFullName: function(newValue, oldValue) {
      if(oldValue === newValue)
        return

      this.nominations.filters.nomineeFullName = newValue
      this.refresh()
    },
    selectedStartDate(newStart, oldStart) {
      this.dateValueChanged(newStart, oldStart);
    },
    selectedEndDate(newEnd, oldEnd) {
      this.dateValueChanged(newEnd, oldEnd);
    },
    organizations: function(newValue, oldValue) {
      this.nominations.filters.organizations = newValue
      this.refresh()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/containers';

.staff-nominations-overview {
  @include screen-container($mobile-padding: 1rem 0.5rem, $desktop-max-width: 112rem);

  &__inner {
    max-width: 100%;
  }

  @media (min-width: $mobile-break-point) {
    &__inner {
      display: flex;
    }

    &__filters {
      flex: 1;
      padding: 1rem 2rem 0 1rem;
      min-width: 16rem;
    }

    &__table {
      flex: 8;
      overflow-x: auto;
    }
  }
}

// Table Styles
.header-row {
  background-color: $soft-gray;

  & > .md-table-head {
    font-size: 0.9rem;
  }
}

.body-row {
  & > .md-table-cell {
    border-top: 1px solid $soft-gray;
    padding: 0.5rem;
  }

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}

.warning-icon {
  color: $ruby;
}

.gray-row {
  background-color: $mid-gray;

  &:hover {
    background-color: $gray;
  }
}

.updated-icons {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}

// Filter UI

.FilterAspect {
  margin-bottom: 32px;
}

.FilterAspect__Title {
  margin-bottom: 0;
}

.FilterAspect__Clear {
  color: gray;
}

.DateRange {
  margin: 0;
}

.Report__Filter__DateRange-field, .Report__Filter__DateRange-picker {
  margin: 0;
}
</style>
